import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import axios from "axios";
import { API_URI } from "../../../constants/GlobalConstants";
import { useSelector } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  Button,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import MaterialPriceSkeletonLoader from "./MaterialPricingSkeleton";

const MaterialPricingComponent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pricingData, setPricingData] = useState({});
  const [dealerName, setDealerName] = useState("");
  const [loading, setLoading] = useState(true);
  const [updatedPrices, setUpdatedPrices] = useState({});
  const [pricingType, setPricingType] = useState("fixed");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;

  useEffect(() => {
    const fetchPricingData = async () => {
      try {
        const response = await axios.get(
          `${API_URI}/api/dealer/dealers/${id}/material-pricing`,
          {
            headers: {
              Authorization: `Bearer ${userInfo.token}`,
            },
          }
        );
        setPricingData(response.data.pricingData);
        setDealerName(response.data.dealerName);
        const firstCategory = Object.values(response.data.pricingData)[0];
        if (firstCategory && firstCategory[0]?.pricingType) {
          setPricingType(firstCategory[0].pricingType);
        }
      } catch (error) {
        console.error("Failed to load material pricing:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPricingData();
  }, [id, userInfo.token]);

  const handlePriceChange = (itemId, value) => {
    setUpdatedPrices((prevPrices) => ({
      ...prevPrices,
      [itemId]: value,
    }));
  };

  const handlePricingTypeChange = (event) => {
    setPricingType(event.target.value);
  };

  const handleSellingPriceClick = (inventoryItemId) => {
    navigate(`/inventory/view-item/${inventoryItemId}`);
  };

  const saveUpdatedPrices = async () => {
    try {
      const updateData = Object.values(pricingData).flat().map((item) => ({
        itemId: item.inventoryItem,
        price:
          updatedPrices[item.inventoryItem] !== undefined
            ? parseFloat(updatedPrices[item.inventoryItem])
            : item.price || 0,
        pricingType,
      }));

      console.log("Sending updateData:", updateData);

      await axios.put(
        `${API_URI}/api/dealer/dealers/${id}/update-pricing`,
        { pricingUpdates: updateData },
        {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        }
      );

      setSnackbarMessage("Pricing updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to update pricing:", error);
      setSnackbarMessage("Failed to update pricing. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Light mode styles
  const lightModeStyles = {
    container: {
      padding: "30px",
      maxWidth: "1200px",
      margin: "auto",
      backgroundColor: "#FFFFFF",
      borderRadius: "12px",
      color: "#333333",
    },
    title: {
      fontWeight: 600,
      color: "#333333",
      marginBottom: "24px",
    },
    categoryTitle: {
      fontWeight: 500,
      color: "#333333",
      marginBottom: "16px",
      borderLeft: "4px solid #3f51b5",
      paddingLeft: "12px",
    },
    tableContainer: {
      maxWidth: "100%",
      marginBottom: "28px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      borderRadius: "8px",
      backgroundColor: "#FFFFFF",
    },
    tableHead: {
      backgroundColor: "#f5f5f5",
    },
    tableHeadCell: {
      fontWeight: 600,
      color: "#333333",
      borderBottom: "2px solid #e0e0e0",
    },
    tableRow: {
      "&:hover": {
        backgroundColor: "#f9f9f9",
      },
      borderBottom: "1px solid #e0e0e0",
    },
    tableCell: {
      color: "#333333",
      borderBottom: "1px solid #e0e0e0",
    },
    clickableCell: {
      color: "#3f51b5",
      cursor: "pointer",
      textDecoration: "underline",
      "&:hover": {
        color: "#303f9f",
      },
    },
    textField: {
      maxWidth: "120px",
      width: "100%",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#CCCCCC",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#999999",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#3f51b5",
      },
      "& .MuiInputBase-input": {
        color: "#333333",
      },
      backgroundColor: "#FFFFFF",
      borderRadius: "6px",
    },
    textFieldInput: {
      textAlign: "right",
      padding: "8px 12px",
      color: "#333333",
    },
    select: {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#CCCCCC",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#999999",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#3f51b5",
      },
      "& .MuiInputBase-input": {
        color: "#333333",
      },
      "& .MuiInputLabel-root": {
        color: "#666666",
      },
      backgroundColor: "#FFFFFF",
      borderRadius: "8px",
    },
    button: {
      borderRadius: "8px",
      padding: "10px 24px",
      textTransform: "none",
      fontSize: "16px",
      boxShadow: "0 3px 8px rgba(0,0,0,0.2)",
      backgroundColor: "#3f51b5",
      color: "#FFFFFF",
      "&:hover": {
        backgroundColor: "#303f9f",
      },
    },
  };

  if (loading) return <MaterialPriceSkeletonLoader />;

  return (
    <div className="p-4 rounded shadow-sm" style={lightModeStyles.container}>
      <Typography
        variant="h4"
        gutterBottom
        align="center"
        className="mb-4"
        sx={lightModeStyles.title}
      >
        Material Pricing for Dealer: {dealerName}
      </Typography>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <FormControl sx={{ minWidth: 200, marginBottom: "24px" }}>
          <InputLabel sx={{ color: "#666666" }}>Pricing Type</InputLabel>
          <Select
            value={pricingType}
            label="Pricing Type"
            onChange={handlePricingTypeChange}
            sx={lightModeStyles.select}
          >
            <MenuItem value="fixed">Fixed Price</MenuItem>
            <MenuItem value="discount">Discount %</MenuItem>
          </Select>
          <Typography
            variant="caption"
            sx={{
              color: "#666666",
              marginTop: "8px",
              display: "block",
              lineHeight: "1.4",
            }}
          >
            Fixed Price: Set price used for materials. Discount %: Percentage off the selling price, applied in material orders.
          </Typography>
        </FormControl>

        <Button
          variant="contained"
          color="primary"
          onClick={saveUpdatedPrices}
          sx={lightModeStyles.button}
          className="ml-auto"
        >
          Save Changes
        </Button>
      </div>

      {Object.entries(pricingData).map(([category, items]) => (
        <div key={category} className="mb-4">
          <Typography
            variant="h5"
            sx={lightModeStyles.categoryTitle}
            className="d-flex align-items-center"
          >
            {category}
          </Typography>

          <TableContainer
            component={Paper}
            sx={lightModeStyles.tableContainer}
            className="shadow-sm"
          >
            <Table size="small" aria-label="material pricing table">
              <TableHead sx={lightModeStyles.tableHead}>
                <TableRow>
                  <TableCell sx={lightModeStyles.tableHeadCell}>Material</TableCell>
                  <TableCell align="right" sx={lightModeStyles.tableHeadCell}>
                    Selling Price
                  </TableCell>
                  <TableCell align="right" sx={lightModeStyles.tableHeadCell}>
                    {pricingType === "fixed" ? "Price" : "Discount %"}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.inventoryItem}
                    sx={lightModeStyles.tableRow}
                  >
                    <TableCell sx={lightModeStyles.tableCell}>{item.name}</TableCell>
                    <TableCell align="right" sx={lightModeStyles.tableCell}>
                      <span
                        onClick={() => handleSellingPriceClick(item.inventoryItem)}
                        style={lightModeStyles.clickableCell}
                      >
                        ${parseFloat(item.sellingPrice || 0).toFixed(2)}
                      </span>
                    </TableCell>
                    <TableCell align="right" sx={lightModeStyles.tableCell}>
                      <TextField
                        size="small"
                        variant="outlined"
                        value={
                          updatedPrices[item.inventoryItem] !== undefined
                            ? updatedPrices[item.inventoryItem]
                            : item.price || 0
                        }
                        type="number"
                        inputProps={{
                          style: lightModeStyles.textFieldInput,
                          min: 0,
                          ...(pricingType === "discount" ? { max: 100 } : {}),
                        }}
                        sx={lightModeStyles.textField}
                        onChange={(e) =>
                          handlePriceChange(item.inventoryItem, e.target.value)
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      ))}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%", borderRadius: "8px" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default MaterialPricingComponent;